import React, { useEffect, useMemo, useState } from "react";
import { Box, DialogContent, DialogContentText } from "@mui/material";
import Popup from "../../../../../components/common/model/Popup";
import closeIcon from "../../../../../assets/closeModel.png";
import CustomButton from "../../../../../components/common/button/CustomButton";
import StepHeader from "./StepHeader";
import SelectStorageStep from "./SelectStorageStep";
import SelectUnitStep from "./SelectUnitStep";
import { StorageRow, UnitRow } from "./Types";
import useStyles from "../../../../../components/common/model/styles";
import { useDispatch, useSelector } from "react-redux";
import { getAllSitesAction, getSiteUnitListAction } from "../../../../../store/reducers/storageSettingSlice";

const SELECT_STORAGE_STEPS = ["Select Storage", "Select Unit"];
interface ManageSelectStorageProps {
  open: boolean;
  onHide: () => void;
  onAssign: (storage: StorageRow, units: UnitRow[]) => void;
  onStepChange?: (step: number) => void;
}

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  search: string;
}

const ManageSelectStorage: React.FC<ManageSelectStorageProps> = ({
  open,
  onHide,
  onAssign,
  onStepChange,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
   const { sites,siteUnitList,siteUnitPagination } = useSelector(
      (state: any) => state.storageSetting,
    );

    const {singleContractDetail} = useSelector((state: any) => state.contractStorage);
  const [activeStep, setActiveStep] = useState(0);
  const [storageSearch, setStorageSearch] = useState("");
  const [selectedStorage, setSelectedStorage] = useState<StorageRow | null>(
    null,
  );

  const [editableUnits, setEditableUnits] = useState<UnitRow[]>([]);
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
    search: "",
  });

  useEffect(() => {
    dispatch(getAllSitesAction(filterOption));
  }, [filterOption]);

  console.log("sites",sites);

  const filteredStorages = useMemo(
    () =>
      sites.filter((storage:any) =>
        `${storage.site_name} ${storage.site_type}`
          .toLowerCase()
          .includes(storageSearch.toLowerCase()),
      ),
    [storageSearch],
  );

  const units = useMemo(() => editableUnits, [editableUnits]);

  const allUnitsSelected =
    units.length > 0 &&
    units.every((unit) => selectedUnitIds.includes(unit.id));

  const reset = () => {
    setActiveStep(0);
    setStorageSearch("");
    setSelectedStorage(null);
    setEditableUnits([]);
    setSelectedUnitIds([]);
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  useEffect(()=>{
    if(siteUnitList?.length>0){
      setEditableUnits(siteUnitList);
    }
  },[siteUnitList])

  useEffect(() => {
    if (selectedStorage?.id) {
      dispatch(
        getSiteUnitListAction({
          ...filterOption,
          siteId: selectedStorage?.id,
        }),
      );
    }
  }, [dispatch, selectedStorage]);

  const handleSelectStorage = (storageId: string) => {
    const storage = sites.find((item:any) => item.id === storageId) || null;
    setSelectedStorage(storage);
    setSelectedUnitIds([]);
    setActiveStep(1);
    onStepChange?.(2);
  };

const handleToggleAllUnits = () => {
  const availableUnits = units.filter(
    (unit) => unit.status?.toLowerCase() === "available"
  );

  const allAvailableSelected =
    availableUnits.length > 0 &&
    availableUnits.every((unit) =>
      selectedUnitIds.includes(unit.id)
    );

  if (allAvailableSelected) {
    // Deselect only available units
    setSelectedUnitIds((prev) =>
      prev.filter(
        (id) => !availableUnits.some((unit) => unit.id === id)
      )
    );
  } else {
    // Select only available units
    setSelectedUnitIds((prev) => {
      const availableIds = availableUnits.map((unit) => unit.id);

      return Array.from(
        new Set([...prev, ...availableIds])
      );
    });
  }
};

  const handleToggleUnit = (unitId: string) => {
    setSelectedUnitIds((current) =>
      current.includes(unitId)
        ? current.filter((id) => id !== unitId)
        : [...current, unitId],
    );
  };

  const handleBackToStorage = () => {
    setSelectedStorage(null);
    setEditableUnits([]);
    setSelectedUnitIds([]);
    setActiveStep(0);
    onStepChange?.(1);
  };

  const handleAssign = () => {
    if (!selectedStorage || selectedUnitIds.length === 0) return;
    const assignedUnits = units.filter((unit) =>
      selectedUnitIds.includes(unit.id),
    );
    onStepChange?.(3);
    onAssign(selectedStorage, assignedUnits);
    handleClose();
  };

  const handleUnitChange = (
    unitId: string,
    field: keyof UnitRow,
    value: string | boolean | null,
  ) => {
    setEditableUnits((current) =>
      current.map((unit) =>
        unit.id === unitId ? { ...unit, [field]: value } : unit,
      ),
    );
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <SelectStorageStep
          storages={filteredStorages}
          search={storageSearch}
          onSearchChange={setStorageSearch}
          onSelectStorage={handleSelectStorage}
        />
      );
    }

    if (activeStep === 1) {
      return (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <CustomButton
              title="Back"
              variant="outlined"
              onClick={handleBackToStorage}
            />
            <Box sx={{ fontSize: "16px", fontWeight: 700 }}>
              {selectedStorage?.name} Units
            </Box>
            <CustomButton
              title="Assign Unit"
              disabled={selectedUnitIds.length === 0}
              onClick={handleAssign}
            />
          </Box>
          <SelectUnitStep
            contractData={singleContractDetail || {}}
            onFieldChange={() => {}}
            units={units}
            selectedUnitIds={selectedUnitIds}
            allUnitsSelected={allUnitsSelected}
            onToggleUnit={handleToggleUnit}
            onToggleAllUnits={handleToggleAllUnits}
            onContinue={handleAssign}
            onCancel={() => setActiveStep(0)}
            onUnitChange={handleUnitChange}
          />
        </>
      );
    }

    return null;
  };

  return (
    <Popup
      open={open}
      title="Manage Select Storage"
      onHide={handleClose}
      fullWidth
      maxWidth="lg"
      popupBg="#FFFFFF"
      header={
        <img
          onClick={handleClose}
          src={closeIcon}
          className={classes.closeicons}
          style={{ cursor: "pointer" }}
          alt="close"
        />
      }
    >
      <DialogContent sx={{ p: 2 }}>
        <DialogContentText component="div">
          <Box sx={{ mb: 2, overflowX: "auto" }}>
            <StepHeader activeStep={activeStep} steps={SELECT_STORAGE_STEPS} />
          </Box>
          {renderStep()}
        </DialogContentText>
      </DialogContent>
    </Popup>
  );
};

export default ManageSelectStorage;
